import { createSelector as ormSelector } from 'redux-orm'
import orm from 'models'
import getEntities, { createFilteredEntitiesSelector } from 'selectors/entities'

const flatten = (record) => ({ ...record.ref })

// Subscriptions reference a plan by id; resolve it from the session so the UI
// can show the plan's name (and kind) instead of a raw id. The plan is
// sideloaded with the subscription, so it is already in the store.
const flattenSubscription = (session) => (record) => {
  const planId = record.ref.planId
  const plan = (planId != null) ? session.billingPlan.withId(planId) : null
  return {
    ...record.ref,
    planName: plan ? plan.name : null,
    planKind: plan ? plan.kind : null
  }
}

export const getBillingPlans = ormSelector(orm, getEntities, (session) =>
  session.billingPlan.all().toModelArray().map(flatten)
)

export const getBillingSubscriptions = ormSelector(orm, getEntities, (session) =>
  session.billingSubscription.all().toModelArray().map(flattenSubscription(session))
)

export const getBillingSubscription = (id) =>
  ormSelector(orm, getEntities, (session) => {
    const record = session.billingSubscription.withId(id)
    return record ? flattenSubscription(session)(record) : null
  })

// Invoices show the customer's name: the snapshot's customer block wins (it is
// what is printed on the PDF), drafts fall back to the subscription's billable.
// `totalInclVat` is derived from the already serialized excl. total + VAT.
const flattenInvoice = (record) => {
  const subscription = record.subscriptionId
  const priceTotal = record.ref.priceTotal
  const vatTotal = record.ref.vatTotal
  return {
    ...record.ref,
    customerName: record.ref.snapshot?.customer?.name || subscription?.billableName || null,
    totalInclVat: (priceTotal == null) ? null : Number(priceTotal) + Number(vatTotal || 0)
  }
}

export const getBillingInvoices = ormSelector(orm, getEntities, (session) =>
  session.billingInvoice.all().toModelArray().map(flattenInvoice)
)

export const getBillingInvoice = (id) =>
  ormSelector(orm, getEntities, (session) => {
    const record = session.billingInvoice.withId(id)
    if (!record) return null
    const items = record.items.toModelArray().map(flatten)
    return { ...flattenInvoice(record), items }
  })

export const getBillingRuns = ormSelector(orm, getEntities, (session) =>
  session.billingRun.all().toModelArray().map(flatten)
)

export const getFilteredBillingSubscriptions = createFilteredEntitiesSelector(getBillingSubscriptions, 'billingSubscription')

export const getFilteredBillingInvoices = createFilteredEntitiesSelector(getBillingInvoices, 'billingInvoice')

export const getBillingSupplierConfig = ormSelector(orm, getEntities, (session) => {
  const record = session.billingSupplierConfig.all().toModelArray()[0]
  return record ? flatten(record) : null
})
